// src/components/Auth/MultiStepSignup.js - WITH TERMS AND CONDITIONS
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService, SecurityValidator } from '../../services/authService';
import TermsAndConditions from './TermsAndConditions';
import './MultiStepSignup.css';

// Country codes data
const countryCodes = [
  { code: '+234', country: 'Nigeria', flag: '🇳🇬' },
  { code: '+1', country: 'United States', flag: '🇺🇸' },
  { code: '+44', country: 'United Kingdom', flag: '🇬🇧' },
  { code: '+233', country: 'Ghana', flag: '🇬🇭' },
  { code: '+254', country: 'Kenya', flag: '🇰🇪' },
  { code: '+27', country: 'South Africa', flag: '🇿🇦' },
  { code: '+91', country: 'India', flag: '🇮🇳' },
  { code: '+86', country: 'China', flag: '🇨🇳' },
];

const MultiStepSignup = ({ onClose, onSuccess }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);

  // ✅ CHANGED: replaced single `error` string with per-field `fieldErrors` object
  const [fieldErrors, setFieldErrors] = useState({});
  
  // ✅ NEW: Terms and Conditions state
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  
  // ✅ Success modal state
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [registrationResult, setRegistrationResult] = useState(null);
  
  // Form data for all steps
  const [formData, setFormData] = useState({
    surname: '',
    first_name: '',
    middle_name: '',
    referral_code: '',
    password: '',
    confirmPassword: '',
    country_code: '+234',
    phone_number: '',
    email: '',
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(countryCodes[0]);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);

  // Password strength indicator
  const [passwordStrength, setPasswordStrength] = useState({
    hasLength: false,
    hasLetter: false,
    hasNumber: false,
    hasSpecial: false
  });

  // Extract referral code from URL on component mount
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const refCode = queryParams.get('ref') || localStorage.getItem('pending_referral_code');
    
    if (refCode) {
      console.log('🔗 Referral code detected:', refCode);
      setFormData(prev => ({ ...prev, referral_code: refCode }));
    }
  }, [location.search]);

  // Update password strength indicator
  useEffect(() => {
    if (formData.password) {
      setPasswordStrength({
        hasLength: formData.password.length >= 8,
        hasLetter: /[a-zA-Z]/.test(formData.password),
        hasNumber: /\d/.test(formData.password),
        hasSpecial: /[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\\\/`~]/.test(formData.password)
      });
    }
  }, [formData.password]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // ✅ CHANGED: clear only the error for the field being edited
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setFormData(prev => ({ ...prev, country_code: country.code }));
    setShowCountryDropdown(false);
  };

  // ========================================
  // HELPER: render inline error under a field
  // ========================================
  const FieldError = ({ name }) =>
    fieldErrors[name] ? (
      <span className="field-error-message">{fieldErrors[name]}</span>
    ) : null;

  // ========================================
  // STEP VALIDATIONS
  // ========================================
  const validateStep1 = () => {
    // ✅ CHANGED: collect ALL field errors at once instead of stopping at first
    const errors = {};

    try {
      SecurityValidator.validateNameField(formData.surname, 'Surname');
    } catch (err) {
      errors.surname = err.message;
    }

    try {
      SecurityValidator.validateNameField(formData.first_name, 'First name');
    } catch (err) {
      errors.first_name = err.message;
    }

    try {
      SecurityValidator.validateNameField(formData.middle_name, 'Middle name');
    } catch (err) {
      errors.middle_name = err.message;
    }

    if (formData.referral_code) {
      try {
        SecurityValidator.validateReferralCode(formData.referral_code);
      } catch (err) {
        errors.referral_code = err.message;
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateStep2 = () => {
    // ✅ CHANGED: collect ALL field errors at once
    const errors = {};

    try {
      SecurityValidator.validatePassword(formData.password);
    } catch (err) {
      errors.password = err.message;
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateStep3 = () => {
    // ✅ CHANGED: map error to phone_number field
    const errors = {};

    try {
      const fullPhone = `${formData.country_code}${formData.phone_number}`;
      SecurityValidator.validatePhoneNumber(fullPhone);
    } catch (err) {
      errors.phone_number = err.message;
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateStep4 = () => {
    // ✅ CHANGED: map errors to email and terms fields individually
    const errors = {};

    try {
      SecurityValidator.validateEmail(formData.email);
    } catch (err) {
      errors.email = err.message;
    }

    if (!agreedToTerms) {
      errors.terms = 'You must agree to the Terms and Conditions to complete registration';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ========================================
  // NAVIGATION HANDLERS
  // ========================================
  const handleNext = () => {
    let isValid = false;

    if (currentStep === 1) {
      isValid = validateStep1();
    } else if (currentStep === 2) {
      isValid = validateStep2();
    } else if (currentStep === 3) {
      isValid = validateStep3();
    } else if (currentStep === 4) {
      isValid = validateStep4();
    }

    if (isValid) {
      if (currentStep < 4) {
        setCurrentStep(currentStep + 1);
        setFieldErrors({});
      } else {
        handleSubmit();
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setFieldErrors({});
    }
  };

  // ========================================
  // FORM SUBMISSION
  // ========================================
  const handleSubmit = async () => {
    setLoading(true);
    setFieldErrors({});

    try {
      const fullPhone = `${formData.country_code}${formData.phone_number}`;
      
      const result = await authService.register({
        surname: formData.surname,
        first_name: formData.first_name,
        middle_name: formData.middle_name,
        email: formData.email,
        password: formData.password,
        phone_number: fullPhone,
        referral_code: formData.referral_code || undefined,
      });

      console.log('✅ Registration successful:', result);

      // ✅ Store result and show success modal
      setRegistrationResult(result);
      setShowSuccessModal(true);

    } catch (err) {
      console.error('❌ Registration failed:', err);

      // ✅ CHANGED: try to map backend error message back to the correct field.
      // authService already extracts the first backend field error as err.message,
      // so we inspect the message to route it to the right field. If it's truly
      // generic we fall back to a top-level `form` key shown as a banner.
      const msg = err.message || 'Registration failed. Please try again.';
      const lower = msg.toLowerCase();

      if (lower.includes('email')) {
        setFieldErrors({ email: msg });
      } else if (lower.includes('phone')) {
        setFieldErrors({ phone_number: msg });
      } else if (lower.includes('surname')) {
        setFieldErrors({ surname: msg });
      } else if (lower.includes('first name')) {
        setFieldErrors({ first_name: msg });
      } else if (lower.includes('middle name')) {
        setFieldErrors({ middle_name: msg });
      } else if (lower.includes('password')) {
        setFieldErrors({ password: msg });
      } else {
        // Truly generic error — show a form-level banner
        setFieldErrors({ form: msg });
      }
    } finally {
      setLoading(false);
    }
  };

  // ✅ Handle success modal OK button
  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    
    // Call onSuccess callback if provided
    if (onSuccess && registrationResult) {
      onSuccess(registrationResult);
    }
    
    // Close the signup modal and return to landing page
    onClose();
  };

  // ✅ NEW: Terms Modal Component
  const renderTermsModal = () => {
    if (!showTermsModal) return null;

    return (
      <div className="terms-modal-overlay" onClick={() => setShowTermsModal(false)}>
        <div className="terms-modal-container" onClick={(e) => e.stopPropagation()}>
          <div className="terms-modal-header">
            <h2>Terms and Conditions</h2>
            <button 
              className="terms-close-button" 
              onClick={() => setShowTermsModal(false)}
              aria-label="Close terms and conditions"
            >
              ×
            </button>
          </div>
          
          <div className="terms-modal-content">
            <TermsAndConditions />
          </div>
          
          <div className="terms-modal-footer">
            <div className="terms-checkbox-wrapper">
              <input
                type="checkbox"
                id="terms-modal-checkbox"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                className="terms-checkbox-input"
              />
              <label htmlFor="terms-modal-checkbox" className="terms-checkbox-label">
                I agree to the Terms and Conditions
              </label>
            </div>
            <button 
              className="terms-close-modal-button"
              onClick={() => setShowTermsModal(false)}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ✅ Success Modal Component
  const renderSuccessModal = () => {
    if (!showSuccessModal || !registrationResult) return null;

    return (
      <div className="success-modal-overlay">
        <div className="success-modal">
          <div className="success-icon">✅</div>
          <h2>Registration Successful!</h2>
          
          <div className="success-message">
            <p>Welcome to TREXAR, <strong>{registrationResult.user?.first_name}</strong>!</p>
            
            <p className="verification-notice">
              A verification email has been sent to:
            </p>
            
            <p className="email-highlight">
              {registrationResult.user?.email}
            </p>
            
            <div className="success-details">
              <p>✓ Your account has been created successfully</p>
              <p className="instruction">Please check your email and click the verification link to activate your account.</p>
            </div>

            <div className="warning-details">
              <p>⚠️ Important:</p>
              <p className="instruction">
                You won't be able to access all features until your email is verified. 
                Please check your spam folder if you don't see the email within a few minutes.
              </p>
            </div>

            {registrationResult.referral_applied && (
              <div className="referral-success">
                <p>
                  🎉 Referral code <strong>{formData.referral_code}</strong> applied successfully!
                </p>
              </div>
            )}
          </div>

          <button 
            className="success-ok-button" 
            onClick={handleSuccessModalClose}
          >
            OK, Got It!
          </button>
        </div>
      </div>
    );
  };

  // ========================================
  // RENDER STEPS
  // ========================================
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="signup-step">
            <h2>Personal Information</h2>
            <p className="step-description">Let's start with your basic information</p>
            <p className="name-hint">⚠️ If your name has special characters, please remove them. e.g: <span className="name-hint-bad">John-doe ❌</span> &nbsp; <span className="name-hint-good">Johndoe ✅</span></p>

            <div className="form-group">
              <label>Surname <span className="required">*</span></label>
              <input
                type="text"
                name="surname"
                placeholder="Enter your surname"
                value={formData.surname}
                onChange={handleInputChange}
                disabled={loading}
                autoComplete="family-name"
                className={fieldErrors.surname ? 'input-error' : ''}
              />
              {/* ✅ CHANGED: inline error directly under field */}
              <FieldError name="surname" />
            </div>

            <div className="form-group">
              <label>First Name <span className="required">*</span></label>
              <input
                type="text"
                name="first_name"
                placeholder="Enter your first name"
                value={formData.first_name}
                onChange={handleInputChange}
                disabled={loading}
                autoComplete="given-name"
                className={fieldErrors.first_name ? 'input-error' : ''}
              />
              <FieldError name="first_name" />
            </div>

            <div className="form-group">
              <label>Middle Name <span className="required">*</span></label>
              <input
                type="text"
                name="middle_name"
                placeholder="Enter your middle name"
                value={formData.middle_name}
                onChange={handleInputChange}
                disabled={loading}
                autoComplete="additional-name"
                className={fieldErrors.middle_name ? 'input-error' : ''}
              />
              <FieldError name="middle_name" />
            </div>

            <div className="form-group">
              <label>Referral Code (Optional)</label>
              <input
                type="text"
                name="referral_code"
                placeholder="Enter referral code if you have one"
                value={formData.referral_code}
                onChange={handleInputChange}
                disabled={loading}
                className={fieldErrors.referral_code ? 'input-error' : ''}
              />
              {formData.referral_code && !fieldErrors.referral_code && (
                <span className="referral-badge">
                  ✓ Referral code applied
                </span>
              )}
              <FieldError name="referral_code" />
            </div>
          </div>
        );

      case 2:
        return (
          <div className="signup-step">
            <h2>Create Password</h2>
            <p className="step-description">Choose a strong password to secure your account</p>

            <div className="form-group">
              <label>Password <span className="required">*</span></label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  placeholder="Enter password"
                  value={formData.password}
                  onChange={handleInputChange}
                  disabled={loading}
                  autoComplete="new-password"
                  className={fieldErrors.password ? 'input-error' : ''}
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}
                >
                  {showPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
              {/* ✅ CHANGED: inline error directly under password field */}
              <FieldError name="password" />

              {formData.password && (
                <div className="password-strength">
                  <div className={`strength-item ${passwordStrength.hasLength ? 'valid' : 'invalid'}`}>
                    {passwordStrength.hasLength ? '✓' : '○'} At least 8 characters
                  </div>
                  <div className={`strength-item ${passwordStrength.hasLetter ? 'valid' : 'invalid'}`}>
                    {passwordStrength.hasLetter ? '✓' : '○'} Contains letters
                  </div>
                  <div className={`strength-item ${passwordStrength.hasNumber ? 'valid' : 'invalid'}`}>
                    {passwordStrength.hasNumber ? '✓' : '○'} Contains numbers
                  </div>
                  <div className={`strength-item ${passwordStrength.hasSpecial ? 'valid' : 'invalid'}`}>
                    {passwordStrength.hasSpecial ? '✓' : '○'} Contains special characters (!@#$%^&*, etc.)
                  </div>
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Confirm Password <span className="required">*</span></label>
              <div className="password-input-wrapper">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  placeholder="Confirm password"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  disabled={loading}
                  autoComplete="new-password"
                  className={fieldErrors.confirmPassword ? 'input-error' : ''}
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  disabled={loading}
                >
                  {showConfirmPassword ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
              {/* ✅ CHANGED: show inline error OR the live match indicator — never both */}
              {fieldErrors.confirmPassword ? (
                <FieldError name="confirmPassword" />
              ) : (
                formData.confirmPassword && (
                  <span className={`helper-text ${formData.password === formData.confirmPassword ? 'success' : 'error'}`}>
                    {formData.password === formData.confirmPassword ? '✓ Passwords match' : '✗ Passwords do not match'}
                  </span>
                )
              )}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="signup-step">
            <h2>Phone Number</h2>
            <p className="step-description">Enter your phone number for account verification</p>

            <div className="form-group">
              <label>Phone Number <span className="required">*</span></label>
              <div className="phone-input-wrapper">
                <div
                  className="country-selector"
                  onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                >
                  <span className="flag">{selectedCountry.flag}</span>
                  <span className="code">{selectedCountry.code}</span>
                  <span className="dropdown-arrow">▼</span>
                </div>

                {showCountryDropdown && (
                  <div className="country-dropdown">
                    {countryCodes.map((country) => (
                      <div
                        key={country.code}
                        className="country-option"
                        onClick={() => handleCountrySelect(country)}
                      >
                        <span className="flag">{country.flag}</span>
                        <span className="country-name">{country.country}</span>
                        <span className="code">{country.code}</span>
                      </div>
                    ))}
                  </div>
                )}

                <input
                  type="tel"
                  name="phone_number"
                  className={`phone-number-input${fieldErrors.phone_number ? ' input-error' : ''}`}
                  placeholder="8012345678"
                  value={formData.phone_number}
                  onChange={handleInputChange}
                  disabled={loading}
                  autoComplete="tel"
                />
              </div>
              {/* ✅ CHANGED: inline error directly under phone field */}
              <FieldError name="phone_number" />
              {!fieldErrors.phone_number && (
                <span className="helper-text">Enter your phone number without the country code</span>
              )}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="signup-step">
            <h2>Email Address</h2>
            <p className="step-description">We'll send a verification link to your email</p>

            {/* ✅ CHANGED: generic backend error banner — only shown when error isn't field-specific */}
            {fieldErrors.form && (
              <div className="error-message">{fieldErrors.form}</div>
            )}

            <div className="form-group">
              <label>Email Address <span className="required">*</span></label>
              <input
                type="email"
                name="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleInputChange}
                disabled={loading}
                autoComplete="email"
                className={fieldErrors.email ? 'input-error' : ''}
              />
              {/* ✅ CHANGED: inline error directly under email field */}
              <FieldError name="email" />
              {!fieldErrors.email && (
                <span className="helper-text">You'll need to verify this email address</span>
              )}
            </div>

            <div className="summary-box">
              <h3>Registration Summary</h3>
              <div className="summary-item">
                <span>Full Name:</span>
                <strong>{formData.surname} {formData.first_name} {formData.middle_name}</strong>
              </div>
              <div className="summary-item">
                <span>Phone:</span>
                <strong>{formData.country_code}{formData.phone_number}</strong>
              </div>
              <div className="summary-item">
                <span>Email:</span>
                <strong>{formData.email}</strong>
              </div>
              {formData.referral_code && (
                <div className="summary-item">
                  <span>Referral Code:</span>
                  <strong>{formData.referral_code}</strong>
                </div>
              )}
            </div>

            {/* ✅ NEW: Terms and Conditions Checkbox */}
            <div className="terms-agreement-section">
              <div className="terms-checkbox-wrapper">
                <input
                  type="checkbox"
                  id="terms-checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => {
                    setAgreedToTerms(e.target.checked);
                    // ✅ clear terms error as soon as user checks the box
                    if (e.target.checked && fieldErrors.terms) {
                      setFieldErrors(prev => ({ ...prev, terms: '' }));
                    }
                  }}
                  className="terms-checkbox-input"
                  disabled={loading}
                />
                <label htmlFor="terms-checkbox" className="terms-checkbox-label">
                  I agree to the{' '}
                  <button 
                    type="button"
                    className="terms-link"
                    onClick={() => setShowTermsModal(true)}
                    disabled={loading}
                  >
                    Terms and Conditions
                  </button>
                </label>
              </div>
              {/* ✅ CHANGED: inline error directly under terms checkbox */}
              <FieldError name="terms" />
            </div>

          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <div className="multistep-signup-overlay">
        <div className="multistep-signup-container">
          <button className="close-button" onClick={onClose} disabled={loading}>
            ×
          </button>

          <div className="progress-bar">
            <div className={`progress-step ${currentStep >= 1 ? 'active' : ''}`}>
              <div className="step-circle">1</div>
              <span className="step-label">Names</span>
            </div>
            <div className={`progress-line ${currentStep >= 2 ? 'active' : ''}`} />
            <div className={`progress-step ${currentStep >= 2 ? 'active' : ''}`}>
              <div className="step-circle">2</div>
              <span className="step-label">Password</span>
            </div>
            <div className={`progress-line ${currentStep >= 3 ? 'active' : ''}`} />
            <div className={`progress-step ${currentStep >= 3 ? 'active' : ''}`}>
              <div className="step-circle">3</div>
              <span className="step-label">Phone</span>
            </div>
            <div className={`progress-line ${currentStep >= 4 ? 'active' : ''}`} />
            <div className={`progress-step ${currentStep >= 4 ? 'active' : ''}`}>
              <div className="step-circle">4</div>
              <span className="step-label">Email</span>
            </div>
          </div>

          {/* ✅ CHANGED: top-level banner removed. Errors now live inside each step's fields.
              The only remaining banner is `fieldErrors.form` rendered inside case 4 above
              for truly generic backend errors that cannot be tied to any specific field. */}

          <div className="signup-content">{renderStep()}</div>

          <div className="button-group">
            {currentStep > 1 && (
              <button className="back-button" onClick={handleBack} disabled={loading}>
                Back
              </button>
            )}
            <button className="next-button" onClick={handleNext} disabled={loading}>
              {loading ? 'Processing...' : currentStep === 4 ? 'Complete Registration' : 'Next'}
            </button>
          </div>

          <div className="auth-switch">
            Already have an account?
            <button className="switch-link" onClick={onClose} disabled={loading}>
              Login
            </button>
          </div>
        </div>
      </div>

      {/* ✅ Terms Modal */}
      {renderTermsModal()}

      {/* ✅ Success Modal */}
      {renderSuccessModal()}
    </>
  );
};

export default MultiStepSignup;